import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePatterns } from '../store/PatternContext';
import { useCollections } from '../store/CollectionContext';

import { setPage, setPatternType, setCurrentPattern } from '../store/patternManagerSlice';

import PatternCard from './PatternCard';
import SortDropdown, { SortMode } from './SortDropdown';
import ArchiveFilterDropdown, { ArchiveFilter, matchesArchiveFilter } from './ArchiveFilterDropdown';
import { PatternType, StoredPattern } from '../types/pattern.model';

import LeftArrow from "../icons/LeftArrow";
import PlusIcon from '../icons/PlusIcon';
import "./PatternList.css";
import { titleize } from '../utils/utils';

interface PatternListProps {
  patternType: PatternType;
}

const sortByName = (a: StoredPattern, b: StoredPattern): number => {
  if (!a.name && !b.name) return 0;
  if (!a.name) return 1;
  if (!b.name) return -1;
  return a.name.localeCompare(b.name);
};

const sortByRecent = (a: StoredPattern, b: StoredPattern): number =>
  b.updatedAt.localeCompare(a.updatedAt);

const PatternList: React.FC<PatternListProps> = ({ patternType }) => {
  const {patterns} = usePatterns(patternType);
  const {collections} = useCollections();
  const dispatch = useDispatch();

  const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>('active');
  const [sortMode, setSortMode] = useState<SortMode>('recent');
  const visiblePatterns = patterns.filter(pattern => matchesArchiveFilter(pattern, archiveFilter));

  const newPattern = () => dispatch(setPage('new'));

  const editPattern = (pattern: StoredPattern) => {
    dispatch(setPage('edit'));
    dispatch(setPatternType(patternType));
    dispatch(setCurrentPattern(pattern));
  };

  const back = () => dispatch(setPatternType(null));

  const renderGrid = (patternsToRender: StoredPattern[]) => (
    <div className='flex w-full justify-center flex-wrap gap-2'>
      {
        patternsToRender.map((pattern) => (
          <PatternCard key={pattern.id} pattern={pattern} patternType={patternType} onClick={() => editPattern(pattern)} />
        ))
      }
    </div>
  );

  const renderContent = () => {
    if (sortMode === 'collection') {
      const byCollectionId = new Map<string | null, StoredPattern[]>();
      for (const pattern of visiblePatterns) {
        const bucket = byCollectionId.get(pattern.collectionId);
        if (bucket) bucket.push(pattern); else byCollectionId.set(pattern.collectionId, [pattern]);
      }

      const uncategorized = (byCollectionId.get(null) ?? []).sort(sortByRecent);
      const groups = collections
        .map(collection => ({
          collection,
          patterns: (byCollectionId.get(collection.id) ?? []).sort(sortByRecent),
        }))
        .filter(group => group.patterns.length > 0)
        .sort((a, b) => a.collection.name.localeCompare(b.collection.name));

      return (
        <div className="flex flex-col items-center w-full gap-4">
          {groups.map(({ collection, patterns }) => (
            <div key={collection.id} className="flex flex-col items-center w-full">
              <p className="text-sm mb-2">{collection.name}</p>
              {renderGrid(patterns)}
            </div>
          ))}
          {uncategorized.length > 0 && (
            <div className="flex flex-col items-center w-full">
              <p className="text-sm mb-2 text-gray-500">Uncategorized</p>
              {renderGrid(uncategorized)}
            </div>
          )}
        </div>
      );
    }

    const sorted = [...visiblePatterns].sort(sortMode === 'name' ? sortByName : sortByRecent);
    return renderGrid(sorted);
  };

  return (
    <div className='flex flex-col items-center w-full pb-8'>
      <div className='flex items-start w-full gap-2 mt-4 mb-2 px-4 cursor-pointer' onClick={back}>
        <div className="flex items-center"><LeftArrow /></div>
        <p className='text-sm'>All Pattern Types</p>
      </div>
      <div className="flex items-center gap-2 mt-2 mb-2">
        <p className="text-lg">Your {titleize(patternType)} Patterns</p>
        <div className='flex items-center cursor-pointer' onClick={newPattern}><PlusIcon /></div>
      </div>
      <div className="flex w-full justify-between items-center px-4 mt-2 mb-4 gap-2">
        <div className="w-44">
          <SortDropdown sortMode={sortMode} onSortModeSelect={setSortMode} className="border-gray-400 text-gray-600" />
        </div>
        <div className="w-44">
          <ArchiveFilterDropdown filter={archiveFilter} onFilterSelect={setArchiveFilter} className="border-gray-400 text-gray-600" />
        </div>
      </div>
      {renderContent()}
    </div>
  );
}

export default PatternList;
